import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { apiClient } from '@/services/api'
import { Trophy, CheckCircle, BookOpen, ArrowLeft, Loader2 } from 'lucide-react'
import { useTerminology } from '@/hooks/useTerminology'
import type { CourseProgress } from '@/types'

export default function CourseCompletionReport() {
  const { courseCode } = useParams<{ courseCode: string }>()
  const navigate = useNavigate()
  const t = useTerminology()

  const [progress, setProgress] = useState<CourseProgress | null>(null)
  const [courseName, setCourseName] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!courseCode) return
    loadData()
  }, [courseCode])

  const loadData = async () => {
    try {
      const [progressRes, infoRes] = await Promise.all([
        apiClient.getCourseProgress(courseCode!),
        apiClient.getCourseInfoPublic(courseCode!)
      ])
      if (progressRes.success && progressRes.data) {
        setProgress(progressRes.data)
      }
      if (infoRes.success && infoRes.data) {
        setCourseName(infoRes.data.course?.name || 'Course')
      }
    } catch {
      // Redirect if not enrolled
      navigate('/')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-theme-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    )
  }

  if (!progress) return null

  const totalLessons = progress.lessons.length
  const completedLessons = progress.lessons.filter(l => l.completed).length
  const isComplete = progress.enrollment.progress_status === 'completed'

  return (
    <div className="min-h-screen bg-theme-bg flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        <div className="bg-surface-deep rounded-2xl p-8 text-center">
          {isComplete ? (
            <Trophy className="w-20 h-20 text-yellow-400 mx-auto mb-4" />
          ) : (
            <BookOpen className="w-20 h-20 text-blue-400 mx-auto mb-4" />
          )}

          <h1 className="text-3xl font-bold text-theme-text mb-2">
            {isComplete ? 'Congratulations!' : `${t.course.title} Progress`}
          </h1>
          <p className="text-theme-text-muted mb-6">{courseName}</p>

          {isComplete && progress.enrollment.completed_at && (
            <p className="text-sm text-theme-muted mb-6">
              Completed on {new Date(progress.enrollment.completed_at).toLocaleDateString()}
            </p>
          )}

          {/* Progress summary */}
          <div className="bg-surface rounded-xl p-4 mb-6">
            <div className="flex justify-between text-sm text-theme-text-muted mb-2">
              <span>{`${t.lesson.titlePlural} Completed`}</span>
              <span className="text-theme-text font-semibold">{completedLessons} / {totalLessons}</span>
            </div>
            <div className="w-full bg-surface-hover rounded-full h-2">
              <div
                className="bg-green-500 h-2 rounded-full transition-all"
                style={{ width: `${totalLessons > 0 ? (completedLessons / totalLessons) * 100 : 0}%` }}
              />
            </div>
          </div>

          {/* Lesson breakdown */}
          <div className="space-y-2 text-left mb-6">
            {progress.lessons.map((lesson) => (
              <div
                key={lesson.position}
                className="flex items-center gap-3 p-3 bg-surface rounded-lg"
              >
                <CheckCircle className={`w-5 h-5 flex-shrink-0 ${
                  lesson.completed ? 'text-green-400' : 'text-theme-muted'
                }`} />
                <span className={`text-sm flex-1 ${lesson.completed ? 'text-theme-text' : 'text-theme-muted'}`}>
                  {lesson.name}
                </span>
                <span className="text-xs text-theme-muted">{lesson.activity_count} activities</span>
              </div>
            ))}
          </div>

          <button
            onClick={() => navigate('/')}
            className="ds-btn ds-btn-primary mx-auto"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  )
}
